import React from 'react';
import { useTranslation } from 'react-i18next';

import { Panel, Toggle, Button } from '../../../../components';
import { Lead, Row, RowText, RowTitle, RowHint, SaveError, Footer } from './PrivacySection.styles';

export type PrivacySectionProps = {
  /** Marketing & analytics consent — gates Google Analytics + the Facebook Pixel. */
  marketing: boolean;
  dirty: boolean;
  saving: boolean;
  saveError: string | null;
  justSaved: boolean;
  onToggle: () => void;
  onSave: () => void;
};

/**
 * Settings › Privacy. A single "Marketing & analytics" consent switch that gates the runtime
 * Google Analytics + Facebook Pixel loaders app-wide. Modeled on CommsSection; the value is
 * persisted to user-preferences (cross-device) and mirrored to the browser consent record via
 * the `usePrivacyForm` hook.
 */
export const PrivacySection = ({
  marketing,
  dirty,
  saving,
  saveError,
  justSaved,
  onToggle,
  onSave,
}: PrivacySectionProps): React.ReactElement => {
  const { t } = useTranslation();
  const saveLabel = saving ? t('Saving…') : justSaved ? t('Saved ✓') : t('Save changes');

  return (
    <Panel>
      <Lead
        text={t(
          'Choose whether we can use marketing and analytics cookies to measure how our site and campaigns are performing. This mirrors the cookie choice you made on our website, and you can change it here at any time.',
        )}
        tag="paragraph"
      />
      <Row>
        <RowText>
          <RowTitle text={t('Marketing & analytics')} tag="span" />
          <RowHint
            text={t('Loads Google Analytics and the Facebook Pixel. When off, neither one ever runs.')}
            tag="span"
          />
        </RowText>
        <Toggle on={marketing} label="Marketing & analytics" onToggle={onToggle} />
      </Row>
      {saveError ? <SaveError text={saveError} tag="span" /> : null}
      <Footer>
        <Button label={saveLabel} fullWidth disabled={!dirty || saving} onClick={onSave} />
      </Footer>
    </Panel>
  );
};

export default PrivacySection;
